import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { getPlayerEmoji } from '../utils/avatars';
import { buildGameWebSocketUrl } from '../utils/ws';
import { cn } from '../utils/cn';
import { useLanguage } from '../contexts/LanguageContext';

export const GameScreen = ({ gameData, onComplete }) => {
    const { t } = useLanguage();
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [timeLeft, setTimeLeft] = useState(0);
    const [phase, setPhase] = useState('init');
    const [connected, setConnected] = useState(false);
    const [phaseEndTime, setPhaseEndTime] = useState(null);

    const wsRef = useRef(null);
    const chatContainerRef = useRef(null);
    const serverOffsetMs = useRef(0);
    const timerIntervalRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const isComponentMounted = useRef(true);

    // Helper to get display name
    const getDisplayName = useCallback((id) => {
        const tg = window.Telegram?.WebApp;
        const currentId = tg?.initDataUnsafe?.user?.id?.toString();

        if (currentId && id === currentId) return t('game_you');
        if (id.startsWith('bot-')) {
            const idx = gameData.players.findIndex(p => p === id);
            return idx >= 0 ? `${t('game_player')} ${idx + 1}` : t('game_bot');
        }
        const idx = gameData.players.findIndex(p => p === id);
        return idx >= 0 ? `${t('game_player')} ${idx + 1}` : t('game_unknown');
    }, [gameData, t]);

    // Scroll to bottom helper
    const scrollToBottom = useCallback(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, []);

    // Scroll on messages or keyboard open (resize)
    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    // Handle window resize (keyboard open/close)
    useEffect(() => {
        const handleResize = () => {
            // Small delay to allow layout to settle
            setTimeout(scrollToBottom, 50);
            setTimeout(scrollToBottom, 200);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [scrollToBottom]);

    // Connection Logic
    const connect = useCallback(() => {
        if (!gameData?.gameId || !isComponentMounted.current) return;

        // cleanup previous if exists (retry scenario)
        if (wsRef.current) {
            wsRef.current.close();
        }

        const tg = window.Telegram?.WebApp;
        const playerId = tg?.initDataUnsafe?.user?.id?.toString() || 'unknown';
        const url = buildGameWebSocketUrl(gameData.gameId, playerId);

        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
            if (!isComponentMounted.current) return;
            console.log('WS Protected Connected');
            setConnected(true);
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };

        ws.onmessage = (event) => {
            if (!isComponentMounted.current) return;
            try {
                const data = JSON.parse(event.data);

                if (data.type === 'connected') {
                    if (data.server_now) {
                        const serverMs = Date.parse(data.server_now);
                        if (!isNaN(serverMs)) {
                            serverOffsetMs.current = serverMs - Date.now();
                        }
                    }
                    if (data.phase) {
                        handlePhaseChange(data);
                    }
                } else if (data.type === 'message') {
                    setMessages(prev => [...prev, {
                        senderId: data.sender_id,
                        text: data.message,
                        emoji: getPlayerEmoji(data.sender_id, gameData.gameId, gameData.players),
                        displayName: getDisplayName(data.sender_id)
                    }]);
                } else if (data.type === 'phase_change') {
                    handlePhaseChange(data);
                } else if (data.type === 'game_over') {
                    console.log('Game Over detected', data);
                    onComplete({ phase: 'results', data: data });
                }
            } catch (e) {
                console.error('WS Parse error', e);
            }
        };

        ws.onerror = (e) => {
            console.error('WS Error', e);
        };

        ws.onclose = () => {
            console.log('WS Closed');
            if (isComponentMounted.current) {
                setConnected(false);
                // Try reconnect if not cleanly closed by us (component unmount)
                // and if game isn't over (logic check handled by server rejecting or us being unmounted)
                reconnectTimeoutRef.current = setTimeout(() => {
                    console.log('Attempting Reconnect...');
                    connect();
                }, 3000);
            }
        };

    }, [gameData, getDisplayName, onComplete]);


    useEffect(() => {
        isComponentMounted.current = true;
        connect();

        return () => {
            isComponentMounted.current = false;
            if (wsRef.current) wsRef.current.close();
            if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        };
    }, [connect]);


    const handlePhaseChange = useCallback((data) => {
        setPhase(data.phase);

        // SYNC TIME: Update server offset if provided
        if (data.server_now) {
            const serverMs = Date.parse(data.server_now);
            if (!isNaN(serverMs)) {
                serverOffsetMs.current = serverMs - Date.now();
            }
        }

        // Calculate absolute end time
        let endMs = 0;

        if (data.phase_end_at) {
            // Trust server absolute time
            endMs = Date.parse(data.phase_end_at);
        } else if (typeof data.duration === 'number') {
            // Fallback: server 'now' + duration
            // We use serverOffset to align 'now' if provided, or simple Duration
            // Ideally backend always sends phase_end_at
            const now = Date.now() + (serverOffsetMs.current || 0);
            endMs = now + (data.duration * 1000);
        }

        if (endMs > 0) {
            setPhaseEndTime(endMs);

            // Immediate tick
            const now = Date.now() + (serverOffsetMs.current || 0);
            const remaining = Math.max(0, Math.round((endMs - now) / 1000));
            setTimeLeft(remaining);
        } else {
            setPhaseEndTime(null);
            setTimeLeft(0);
        }

        // Check immediate redirection
        if (data.phase === 'voting') {
            // For voting, we optionally show a "Voting starts!" splash or just redirect
            // Current logic: wait 1s then redirect
            setTimeout(() => {
                onComplete({ phase: 'voting', timeLeft: data.duration, ws: wsRef.current });
            }, 1000);
        } else if (data.phase === 'results') {
            // Should have received game_over usually, but if we drop into results phase directly:
            // We might need to fetch results if data doesn't have them
            // Usually game_over handles this.
        }

    }, [onComplete]);

    // Timer Sync Impl
    useEffect(() => {
        if (!phaseEndTime) return;

        const tick = () => {
            const now = Date.now() + (serverOffsetMs.current || 0);
            const diff = phaseEndTime - now;
            const sec = Math.max(0, Math.round(diff / 1000));

            // Haptic on T-3, T-2, T-1 for crucial phases
            if (sec <= 3 && sec > 0 && phase !== 'init') {
                // Check previous value to avoid spamming every tick if sub-second?
                // But this runs 1s interval mostly, plus phaseEndTime logic.
                // To be safe, we can just fire.
                // Ideally we should track if we fired for this second.
                // But let's keep it simple: fire if changes.
                // Actually the interval is 1000ms.
                if (window.Telegram?.WebApp?.HapticFeedback) {
                    // Use specific vibrations?
                    // 3, 2, 1 = warning or heavy?
                    // User asked for "when 3 seconds left".
                    // Maybe just once at 3? Or countdown?
                    // "когда остается 3 секунды" implies countdown.
                    window.Telegram.WebApp.HapticFeedback.impactOccurred('soft');
                }
            }
            // Enhance the "Start Game" vibration?
            // User requested vibration on Game Start. I added that in QueueScreen (MatchFound).

            setTimeLeft(sec);
            if (sec <= 0) {
                // Timer finished
            }
        };

        // Run immediately
        tick();

        timerIntervalRef.current = setInterval(tick, 1000);
        return () => clearInterval(timerIntervalRef.current);
    }, [phaseEndTime, phase]);

    const handleSend = () => {
        if (!inputValue.trim() || !wsRef.current) return;
        wsRef.current.send(JSON.stringify({ type: 'message', message: inputValue.trim() }));
        setInputValue('');
    };

    // Render helpers
    const isCurrentUser = (id) => {
        const tg = window.Telegram?.WebApp;
        return tg?.initDataUnsafe?.user?.id?.toString() === id;
    };

    return (
        <div className="flex flex-col w-full overflow-hidden relative z-10 h-[100dvh]">
            {/* Header: Timer & Status */}
            <div className="shrink-0 px-4 pt-4 pb-2 z-20">
                <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-2xl p-2 backdrop-blur-xl shadow-lg">
                    {/* Status Badge */}
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-900/50 border border-white/5">
                        <span className="text-xl animate-pulse">
                            {phase === 'chat' ? '💬' : phase === 'voting' ? '🗳️' : '🎮'}
                        </span>
                        <span className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                            {phase === 'chat' ? t('game_status_chat') : phase === 'voting' ? t('game_status_voting') : t('game_status_game')}
                        </span>
                    </div>

                    {/* Timer */}
                    <div className={cn(
                        "flex items-center gap-2 px-4 py-1.5 rounded-xl border transition-all duration-500",
                        timeLeft <= 10
                            ? "bg-red-500/20 border-red-500/50 shadow-[0_0_15px_rgba(239,68,68,0.3)] animate-pulse"
                            : "bg-cyan-500/10 border-cyan-500/30"
                    )}>
                        <span className="text-xs text-slate-400 font-bold uppercase mr-1">{t('game_timer')}</span>
                        <span className={cn(
                            "font-mono font-black text-lg tabular-nums leading-none",
                            timeLeft <= 10 ? "text-red-400" : "text-cyan-400"
                        )}>
                            {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                        </span>
                    </div>
                </div>
            </div>

            {/* Players Grid (2 Rows) */}
            <div className="shrink-0 px-4 pb-2 z-10">
                <div className="grid grid-cols-2 gap-2 bg-white/5 p-2 rounded-2xl border border-white/5 max-h-[140px] overflow-y-auto custom-scrollbar">
                    {gameData.players.map((pid, idx) => {
                        const isMe = isCurrentUser(pid);
                        const emoji = getPlayerEmoji(pid, gameData.gameId, gameData.players);
                        return (
                            <div key={pid} className={cn(
                                "flex items-center gap-2 px-3 py-2 rounded-xl border backdrop-blur-md transition-all",
                                isMe
                                    ? "bg-cyan-500/20 border-cyan-500/40 shadow-[0_0_10px_rgba(6,182,212,0.1)]"
                                    : "bg-white/5 border-white/10"
                            )}>
                                <div className="text-lg filter drop-shadow-md">{emoji}</div>
                                <div className="flex flex-col min-w-0">
                                    <span className={cn(
                                        "text-[10px] font-bold uppercase tracking-wider truncate",
                                        isMe ? "text-cyan-300" : "text-slate-400"
                                    )}>
                                        {isMe ? t('game_you') : `${t('game_player')} ${idx + 1}`}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Chat Area (Standard Flex) */}
            <div className="flex-1 min-h-0 flex flex-col px-4 pb-2">
                <div
                    className="flex-1 bg-black/20 border border-white/5 rounded-2xl overflow-hidden relative flex flex-col"
                >
                    <div
                        ref={chatContainerRef}
                        className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar scroll-smooth w-full"
                    >
                        {messages.length === 0 && (
                            <div className="h-full flex flex-col items-center justify-center space-y-4 opacity-40">
                                <div className="w-20 h-20 bg-gradient-to-br from-slate-800 to-slate-900 rounded-full flex items-center justify-center border border-white/5">
                                    <span className="text-4xl">💭</span>
                                </div>
                                <p className="text-sm text-slate-400 font-medium">{t('game_silence')}</p>
                            </div>
                        )}

                        {messages.map((msg, i) => {
                            const isMe = isCurrentUser(msg.senderId);
                            return (
                                <div key={i} className={cn(
                                    "flex flex-col max-w-[85%] animate-fade-in-up shrink-0",
                                    isMe ? "ml-auto items-end" : "mr-auto items-start"
                                )}>
                                    {/* Name/Avatar Label */}
                                    <div className="flex items-center gap-2 mb-1 px-1">
                                        {!isMe && <span className="text-xs">{msg.emoji}</span>}
                                        <span className={cn(
                                            "text-[10px] font-bold uppercase tracking-wide",
                                            isMe ? "text-cyan-400" : "text-slate-500"
                                        )}>
                                            {msg.displayName}
                                        </span>
                                    </div>

                                    {/* Bubble */}
                                    <div className={cn(
                                        "px-4 py-3 text-sm leading-relaxed shadow-lg backdrop-blur-sm break-words relative",
                                        isMe
                                            ? "bg-gradient-to-br from-cyan-600 to-blue-700 text-white rounded-2xl rounded-tr-sm border border-cyan-400/30"
                                            : "bg-slate-800/80 text-slate-200 rounded-2xl rounded-tl-sm border border-white/10"
                                    )}>
                                        {msg.text}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Input Floating Bar */}
            <div className="shrink-0 px-4 pb-6 pt-0 z-20">
                <div className="flex items-center gap-2 p-1.5 bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-[24px] shadow-2xl relative transition-all duration-300 focus-within:border-cyan-500/50 focus-within:shadow-[0_0_20px_rgba(6,182,212,0.15)] group">
                    <input
                        className="flex-1 bg-transparent border-none text-sm text-white px-4 py-3 focus:ring-0 outline-none placeholder:text-slate-500 font-medium"
                        placeholder={t('game_input_placeholder')}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                        disabled={phase !== 'chat'}
                    />
                    <Button
                        variant="primary"
                        size="icon"
                        onClick={handleSend}
                        disabled={phase !== 'chat'}
                        className={cn(
                            "w-10 h-10 rounded-full shrink-0 flex items-center justify-center transition-transform active:scale-95",
                            (!inputValue.trim() || phase !== 'chat') && "opacity-50 grayscale"
                        )}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="22" y1="2" x2="11" y2="13"></line>
                            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                        </svg>
                    </Button>
                </div>
            </div>
        </div>
    );
};
